import { IWhitelistedUser, TComment, WhitelistedUserDealSide } from '@valuecase/common'
import { useCommentTitle } from './useCommentTitle'
import UserAvatarWithNotificationSettings from '../../features/users/user-avatar-with-notification-settings'
import { ReactNode, useMemo } from 'react'
import { DateTime } from 'luxon'
import { IonIcon } from '../../Icons/IonIcon'
import { cn } from '../../lib/utils'
import DOMPurify from 'dompurify'
import { Button2 } from '../../components/ui'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/tooltip'

interface CommentProps {
  comment: TComment
  whitelistedUser?: IWhitelistedUser | null
  optionsRender?: ReactNode
  onOpenBlock?: () => void
}

const Comment = ({ comment, whitelistedUser, optionsRender, onOpenBlock }: CommentProps) => {
  const title = useCommentTitle({ comment })

  const formattedDate = useMemo(() => {
    if (!comment.createdAt) return ''
    const date = DateTime.fromISO(comment.createdAt)
    return date.toRelative()
  }, [comment.createdAt])

  const userInfo = useMemo(() => {
    const isAnonymous = !!comment.isAnonymous

    return {
      firstName: whitelistedUser?.firstName || comment.firstName || '',
      lastName: whitelistedUser?.lastName || comment.lastName || '',
      email: whitelistedUser?.email || comment.email || '',
      name: isAnonymous
        ? ''
        : `${whitelistedUser?.firstName || comment.firstName || ''} ${whitelistedUser?.lastName || comment.lastName || ''}`.trim(),
      WhitelistedUserNotificationsSettings: whitelistedUser?.WhitelistedUserNotificationsSettings,
      dealSide: whitelistedUser?.dealSide || WhitelistedUserDealSide.SELLER,
      isAnonymous,
    }
  }, [comment, whitelistedUser])

  const renderMessage = useMemo(() => {
    const urlRegex = /(https?:\/\/[^\s]+)/g
    const replacedText = comment.message.replace(urlRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noreferrer" class="hover:underline text-primary-s5">${url}</a>`
    })
    return DOMPurify.sanitize(replacedText, {
      ALLOWED_TAGS: ['a'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    })
  }, [comment.message])

  const renderTitle = useMemo(() => {
    if (Array.isArray(title)) {
      return (
        <div className='flex flex-wrap items-center '>
          {title.map((item, index) => (
            <span key={index} className='inline-flex items-center '>
              {index > 0 && (
                <IonIcon name='chevron-forward' className={cn('mx-1 text-grey-s4 h-3 w-3')} />
              )}
              <span className={cn('text-xs', index > 0 ? 'text-grey-s5' : 'text-grey-s4')}>
                {item}
              </span>
            </span>
          ))}
        </div>
      )
    }
    return <span className='text-grey-s4 text-xs'>{title}</span>
  }, [title])

  return (
    <div className='flex flex-col w-full group'>
      {title && (
        <div className='flex w-full px-5 py-[6px] rounded-t-md bg-brand-grey-s1 group-hover:bg-blue-s2'>
          <div className='flex items-center gap-1 flex-wrap '>{renderTitle}</div>
        </div>
      )}
      <div className='px-3 py-3 bg-white flex flex-col gap-3 rounded-b-md'>
        <div className='flex justify-between gap-2'>
          <div className='flex gap-2 items-start'>
            <UserAvatarWithNotificationSettings user={userInfo} size='SMALL' />
            <div className='flex flex-col gap-1 w-full'>
              <span className='text-sm font-semibold'>
                {userInfo.isAnonymous ? userInfo.email : userInfo.name || userInfo.email}
              </span>
              <span className='text-xs text-grey-s4'>{formattedDate}</span>
            </div>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button2
                size={'smallIcon'}
                variant={'subtle'}
                leadingIcon='c_open'
                onClick={onOpenBlock}
                className='opacity-0 group-hover:opacity-100 transition-opacity'
              />
            </TooltipTrigger>
            <TooltipContent>Open block</TooltipContent>
          </Tooltip>

          {optionsRender}
        </div>
        <div
          className='text-sm text-grey-s6 whitespace-pre-wrap break-words'
          dangerouslySetInnerHTML={{ __html: renderMessage }}
        />
      </div>
    </div>
  )
}

export default Comment
